import React from 'react';

/**
 * Generic React Error Boundary.
 *
 * Catches runtime errors in the subtree and shows a friendly fallback UI
 * instead of crashing the entire page. Wrap any major section with this
 * component to isolate failures.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <SomeSection />
 *   </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] Uncaught error:', error, info.componentStack);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
          <div className="max-w-md">
            <div className="text-5xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-foreground mb-2">
              {this.props.title || 'Something went wrong'}
            </h2>
            <p className="text-muted-foreground mb-6">
              {this.props.description ||
                'This section encountered an unexpected error. You can try refreshing the page or continue browsing.'}
            </p>
            <button
              onClick={this.handleRetry}
              className="px-6 py-2 rounded-lg bg-gradient-to-r from-teal-600 to-amber-500 text-white font-medium hover:opacity-90 transition-opacity"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
